import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from '@dr.pogodin/react-helmet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, ChevronLeft, Mail, Trash2, Send, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveOrg } from '@/hooks/useActiveOrg';
import type { OrgRole } from '@/hooks/useCurrentOrg';

interface OrgRow {
  id: string;
  name: string;
  slug: string;
  created_by: string;
}

interface MemberRow {
  id: string;
  user_id: string;
  role: OrgRole;
  email?: string;
  display_name?: string;
}

interface InviteRow {
  id: string;
  email: string;
  role: OrgRole;
  status: string;
  expires_at: string;
  created_at: string;
}

const ROLE_OPTIONS: OrgRole[] = ['owner', 'admin', 'instructor', 'student'];

export default function OrgDetail() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { memberships, refetch: refetchActive } = useActiveOrg();

  const myRole = memberships.find((m) => m.org_id === orgId)?.role;
  const isOwnerOrAdmin = myRole === 'owner' || myRole === 'admin';

  const [org, setOrg] = useState<OrgRow | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<OrgRole>('instructor');
  const [sending, setSending] = useState(false);

  const load = async () => {
    if (!orgId) return;
    setLoading(true);
    const [{ data: o }, { data: m }, { data: inv }] = await Promise.all([
      supabase.from('organizations').select('id, name, slug, created_by').eq('id', orgId).maybeSingle(),
      supabase.from('org_members').select('id, user_id, role').eq('org_id', orgId),
      supabase.from('organization_invitations').select('id, email, role, status, expires_at, created_at').eq('org_id', orgId).order('created_at', { ascending: false }),
    ]);
    if (!o) {
      toast.error('Organization not found');
      navigate('/orgs');
      return;
    }
    setOrg(o as OrgRow);
    const memberRows = (m ?? []) as MemberRow[];
    const ids = memberRows.map((mm) => mm.user_id);
    if (ids.length) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', ids);
      const pmap = new Map(
        (profiles ?? []).map((p: any) => [p.id, [p.first_name, p.last_name].filter(Boolean).join(' ') || null]),
      );
      setMembers(memberRows.map((mm) => ({ ...mm, display_name: pmap.get(mm.user_id) ?? undefined })));
    } else {
      setMembers(memberRows);
    }
    setInvites((inv ?? []) as InviteRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const sendInvite = async () => {
    if (!user || !org) return;
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setSending(true);
    const { data: inv, error } = await supabase
      .from('organization_invitations')
      .insert({ org_id: org.id, email, role: inviteRole, invited_by: user.id })
      .select('id, token')
      .single();
    if (error || !inv) {
      setSending(false);
      toast.error(error?.message ?? 'Could not create invitation');
      return;
    }
    const inviterName =
      [user.user_metadata?.first_name, user.user_metadata?.last_name].filter(Boolean).join(' ') ||
      user.email ||
      'A teammate';

    const { error: fnErr } = await supabase.functions.invoke('send-org-invitation', {
      body: {
        invitationId: inv.id,
        email,
        orgName: org.name,
        inviterName,
        role: inviteRole,
        token: inv.token,
      },
    });
    setSending(false);
    if (fnErr) {
      toast.message('Invitation created. Email could not be sent — share the accept link manually.', {
        description: fnErr.message,
      });
    } else {
      toast.success(`Invitation sent to ${email}`);
    }
    setInviteEmail('');
    load();
  };

  const revokeInvite = async (id: string) => {
    const { error } = await supabase
      .from('organization_invitations')
      .update({ status: 'revoked' })
      .eq('id', id);
    if (error) toast.error(error.message);
    else { toast.success('Invitation revoked'); load(); }
  };

  const updateMemberRole = async (memberId: string, role: OrgRole) => {
    const { error } = await supabase
      .from('org_members')
      .update({ role })
      .eq('id', memberId);
    if (error) toast.error(error.message);
    else { toast.success('Role updated'); load(); refetchActive(); }
  };

  const removeMember = async (memberId: string) => {
    if (!confirm('Remove this member from the organization?')) return;
    const { error } = await supabase.from('org_members').delete().eq('id', memberId);
    if (error) toast.error(error.message);
    else { toast.success('Member removed'); load(); refetchActive(); }
  };

  if (loading || !org) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-3xl py-10">
      <Helmet>
        <title>{org.name} – Organization</title>
        <meta name="description" content={`Manage members and invitations for ${org.name}.`} />
      </Helmet>

      <Link to="/orgs" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4">
        <ChevronLeft className="h-4 w-4 mr-1" /> All organizations
      </Link>

      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">{org.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">/{org.slug}</p>
        </div>
        <Button variant="outline" size="sm" asChild>
          <Link to="/org/settings/practice-items">
            <Settings className="mr-2 h-4 w-4" /> Practice settings
          </Link>
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Members ({members.length})</CardTitle></CardHeader>
        <CardContent className="p-0 divide-y">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-4">
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{m.display_name ?? m.user_id.slice(0, 8)}</div>
              </div>
              {isOwnerOrAdmin && m.user_id !== user?.id ? (
                <Select
                  value={m.role}
                  onValueChange={(v) => updateMemberRole(m.id, v as OrgRole)}
                >
                  <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Badge variant="secondary" className="capitalize">{m.role}</Badge>
              )}
              {isOwnerOrAdmin && m.user_id !== user?.id && (
                <Button variant="ghost" size="icon" onClick={() => removeMember(m.id)} aria-label="Remove member">
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {isOwnerOrAdmin && (
        <Card>
          <CardHeader><CardTitle className="text-base">Invitations</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                placeholder="teammate@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="flex-1"
              />
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as OrgRole)}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={sendInvite} disabled={sending || !inviteEmail.trim()}>
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Invite
              </Button>
            </div>

            <div className="divide-y border rounded-md">
              {invites.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground text-center">No invitations yet.</div>
              ) : invites.map((i) => (
                <div key={i.id} className="flex items-center gap-3 p-3 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{i.email}</div>
                    <div className="text-xs text-muted-foreground">
                      <span className="capitalize">{i.role}</span> · expires {new Date(i.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge
                    variant={i.status === 'pending' ? 'default' : 'secondary'}
                    className="capitalize"
                  >
                    {i.status}
                  </Badge>
                  {i.status === 'pending' && (
                    <Button variant="ghost" size="icon" onClick={() => revokeInvite(i.id)} aria-label="Revoke invitation">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
